import {createSelector} from '@ngrx/store';
import {
  selectAssetsByVersion,
  selectOfftakesByVersion,
  selectPriceDatasets,
  selectPriceDatasetsLoaded,
  selectSimulationResults,
  selectVersion,
  selectVersions,
  selectVersionsLoaded,
} from './portfolio-simulator.reducer';
import {PORTFOLIO_LIVE_VERSION_NAME} from '../../../shared/model/constants';
import {ComboboxItem} from '../models/combobox-item';

export const selectAssetsForCurrentVersion = createSelector(
  selectAssetsByVersion,
  selectVersion,
  (byVersion, version) => byVersion[version] ?? [],
);

export const selectShouldLoadAssets = createSelector(
  selectAssetsByVersion,
  selectVersion,
  (byVersion, version) => !(version in byVersion),
);

export const selectOfftakesForCurrentVersion = createSelector(
  selectOfftakesByVersion,
  selectVersion,
  (byVersion, version) => byVersion[version] ?? [],
);

export const selectShouldLoadOfftakes = createSelector(
  selectOfftakesByVersion,
  selectVersion,
  (byVersion, version) => !(version in byVersion),
);

// versionsLoading is intentionally ignored: if two updateInputs arrive while a fetch is in flight,
// the guard lets both through. The switchMap in loadVersions$ cancels the first HTTP request,
// ensuring only the latest result is applied.
export const selectShouldLoadVersions = createSelector(
  selectVersionsLoaded,
  (loaded) => !loaded,
);

export const selectVersionItems = createSelector(selectVersions, (versions) =>
  [...versions]
    .sort((a, b) =>
      a.versionName === PORTFOLIO_LIVE_VERSION_NAME ? -1 : b.versionName === PORTFOLIO_LIVE_VERSION_NAME ? 1 : 0,
    )
    .map((v) => ({
      versionName: v.versionName,
      versionPubliDate: v.versionPubliDate,
    })),
);

// priceDatasetsLoading is intentionally ignored: same pattern as selectShouldLoadVersions —
// switchMap in loadPriceDatasets$ cancels any in-flight request when a newer action arrives.
export const selectShouldLoadPriceDatasets = createSelector(
  selectPriceDatasetsLoaded,
  (loaded) => !loaded,
);

export const selectPriceDatasetItems = createSelector(
  selectPriceDatasets,
  (priceDatasets): ComboboxItem[] =>
    priceDatasets.map((pd) => ({
      value: pd.priceDataset,
      label: pd.priceDataset,
    })),
);

// ─── Simulation results filter options (derived from data) ───────────────────

export const selectAvailableContinents = createSelector(
  selectSimulationResults,
  (results) =>
    [...new Set(results.map((r) => r.continent).filter((c): c is string => c != null))].sort((a, b) =>
      a.localeCompare(b),
    ),
);

export const selectAvailableProjectTypes = createSelector(
  selectSimulationResults,
  (results) =>
    [...new Set(results.map((r) => r.projectType).filter((t): t is string => t != null))].sort((a, b) =>
      a.localeCompare(b),
    ),
);

export const selectAvailableProjectStatuses = createSelector(
  selectSimulationResults,
  (results) =>
    [...new Set(results.map((r) => r.renStatus).filter((s): s is string => s != null))].sort((a, b) =>
      a.localeCompare(b),
    ),
);

export const selectContinentToCountries = createSelector(
  selectSimulationResults,
  (results) => {
    const map: Record<string, string[]> = {};
    for (const r of results) {
      if (r.continent == null || r.country == null) continue;
      if (!map[r.continent]) map[r.continent] = [];
      if (!map[r.continent].includes(r.country)) map[r.continent].push(r.country);
    }
    for (const countries of Object.values(map)) countries.sort((a, b) => a.localeCompare(b));
    return map;
  },
);

export const selectCountryToBiddingZones = createSelector(
  selectSimulationResults,
  (results) => {
    const map: Record<string, string[]> = {};
    for (const r of results) {
      if (r.country == null || r.biddingZone == null) continue;
      if (!map[r.country]) map[r.country] = [];
      if (!map[r.country].includes(r.biddingZone)) map[r.country].push(r.biddingZone);
    }
    for (const zones of Object.values(map)) zones.sort((a, b) => a.localeCompare(b));
    return map;
  },
);
